import React, { useState, useEffect } from 'react';
import {
  collection,
  query,
  getDocs,
  orderBy,
  doc,
  updateDoc,
  limit,
  where,
  Timestamp
} from 'firebase/firestore';
import { db } from '../../../firebase/config';
import {
  ShoppingBag,
  Package,
  Truck,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  Search,
  Filter,
  Download,
  Eye,
  MessageCircle,
  IndianRupee,
  Calendar,
  ChevronRight,
  ChevronDown,
  RefreshCw,
  FileText,
  Building,
  User,
  Phone,
  Mail,
  Users,
  TrendingUp,
  BarChart,
  Shield,
  Edit,
  Trash2,
  MoreVertical,
  ExternalLink,
  Palette // NEW: Import Palette icon
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const AdminOrdersPage = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    confirmed: 0,
    processing: 0,
    shipped: 0,
    delivered: 0,
    cancelled: 0,
    totalRevenue: 0
  });

  // Status configurations
  const statusConfig = {
    pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
    confirmed: { label: 'Confirmed', color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
    processing: { label: 'Processing', color: 'bg-purple-100 text-purple-800', icon: RefreshCw },
    shipped: { label: 'Shipped', color: 'bg-indigo-100 text-indigo-800', icon: Truck },
    delivered: { label: 'Delivered', color: 'bg-green-100 text-green-800', icon: CheckCircle },
    cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-800', icon: XCircle }
  };

  // Format INR currency
  const formatINR = (amount) => {
    if (!amount) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Function to parse colors from string to array
  const parseColors = (colorsString) => {
    if (!colorsString || colorsString.trim() === '') return [];
    
    // Split by common separators: comma, plus, ampersand, pipe, slash
    const colors = colorsString
      .split(/[,+&|/]/)
      .map(color => color.trim())
      .filter(color => color.length > 0);
    
    return colors;
  };

  // Fetch ALL orders from database
  const fetchAllOrders = async () => {
    try {
      setLoading(true);
      console.log('Fetching ALL orders from database...');

      // Fetch from saved-products collection
      const savedProductsRef = collection(db, 'saved-products');
      
      // Get ALL orders (no where clause for specific user)
      const q = query(
        savedProductsRef,
        orderBy('createdAt', 'desc'), // Sort by latest first
        limit(500) // Increase limit for admin
      );

      const querySnapshot = await getDocs(q);
      const ordersList = [];
      let totalRevenue = 0;

      querySnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        
        // Parse colors if they exist
        const productColors = data.productColors || '';
        const selectedColor = data.selectedColor || '';
        const availableColors = parseColors(productColors);
        
        ordersList.push({
          id: docSnap.id,
          ...data,
          
          // Color Information - NEW
          productColors: productColors,
          selectedColor: selectedColor,
          availableColors: availableColors,
          
          // Ensure dates are properly handled
          createdAt: data.createdAt || data.savedAt || new Date(),
          updatedAt: data.updatedAt || new Date()
        });

        // Calculate total revenue from delivered orders
        if (data.orderStatus === 'delivered' && data.totalAmount) {
          totalRevenue += parseFloat(data.totalAmount) || 0;
        }
      });

      console.log(`Successfully loaded ${ordersList.length} total orders`);

      // Calculate statistics
      const statsData = {
        total: ordersList.length,
        pending: ordersList.filter(o => o.orderStatus === 'pending').length,
        confirmed: ordersList.filter(o => o.orderStatus === 'confirmed').length,
        processing: ordersList.filter(o => o.orderStatus === 'processing').length,
        shipped: ordersList.filter(o => o.orderStatus === 'shipped').length,
        delivered: ordersList.filter(o => o.orderStatus === 'delivered').length,
        cancelled: ordersList.filter(o => o.orderStatus === 'cancelled').length,
        totalRevenue: totalRevenue
      };

      setOrders(ordersList);
      setStats(statsData);

    } catch (error) {
      console.error('Error fetching all orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  // Also fetch from orders collection if you have separate collection
  const fetchFromOrdersCollection = async () => {
    try {
      const ordersRef = collection(db, 'orders');
      const q = query(ordersRef, orderBy('createdAt', 'desc'), limit(500));
      const querySnapshot = await getDocs(q);
      const ordersList = [];

      querySnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        ordersList.push({
          id: docSnap.id,
          ...data,
          collection: 'orders'
        });
      });

      console.log(`Loaded ${ordersList.length} orders from orders collection`);
      return ordersList;
    } catch (error) {
      console.error('Error fetching from orders collection:', error);
      return [];
    }
  };

  // Update order status (Admin action)
  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const orderRef = doc(db, 'saved-products', orderId);
      await updateDoc(orderRef, {
        orderStatus: newStatus,
        updatedAt: new Date(),
        updatedBy: 'admin'
      });

      toast.success(`Order status updated to ${newStatus}`);
      fetchAllOrders(); // Refresh orders
    } catch (error) {
      console.error('Error updating order status:', error);
      toast.error('Failed to update order status');
    }
  };

  // Delete order (Admin action)
  const deleteOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this order? This action cannot be undone.')) {
      return;
    }

    try {
      // If using Firestore delete
      // await deleteDoc(doc(db, 'saved-products', orderId));
      
      // Or mark as deleted
      const orderRef = doc(db, 'saved-products', orderId);
      await updateDoc(orderRef, {
        isDeleted: true,
        deletedAt: new Date(),
        deletedBy: 'admin'
      });

      toast.success('Order marked as deleted');
      fetchAllOrders();
    } catch (error) {
      console.error('Error deleting order:', error);
      toast.error('Failed to delete order');
    }
  };

  // Filter orders
  const filteredOrders = orders.filter(order => {
    const matchesSearch = 
      (order.productName?.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
      (order.orderId?.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
      (order.buyerName?.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
      (order.sellerName?.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
      (order.buyerEmail?.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
      (order.selectedColor?.toLowerCase().includes(searchTerm.toLowerCase()) || false) || // NEW: Search by selected color
      (order.productColors?.toLowerCase().includes(searchTerm.toLowerCase()) || false); // NEW: Search by all colors

    const matchesStatus = statusFilter === 'all' || order.orderStatus === statusFilter;
    
    return matchesSearch && matchesStatus;
  });
  console.log(`Filtered orders count: `,filteredOrders);

  // Toggle order details
  const toggleOrderDetails = (orderId) => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  // Fetch all orders on component mount
  useEffect(() => {
    fetchAllOrders();
  }, []);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-gray-300 rounded w-48"></div>
            <div className="h-10 bg-gray-300 rounded"></div>
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-32 bg-gray-300 rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center">
                <ShoppingBag className="h-8 w-8 mr-3 text-orange-600" />
                Admin Orders Dashboard
              </h1>
              <p className="text-gray-600 ml-11 mt-1">
                Manage all orders across the platform
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={fetchAllOrders}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center text-sm font-medium"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh Orders
              </button>
              <button
                onClick={() => navigate('/admin')}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center text-sm font-medium"
              >
                Back to Dashboard
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 mb-6">
            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
              <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
              <div className="text-sm text-gray-600 flex items-center">
                <ShoppingBag className="h-3 w-3 mr-1" />
                Total Orders
              </div>
            </div>
            <div className="bg-yellow-50 p-4 rounded-xl border border-yellow-200 shadow-sm">
              <div className="text-2xl font-bold text-yellow-700">{stats.pending}</div>
              <div className="text-sm text-yellow-600 flex items-center">
                <Clock className="h-3 w-3 mr-1" />
                Pending
              </div>
            </div>
            <div className="bg-blue-50 p-4 rounded-xl border border-blue-200 shadow-sm">
              <div className="text-2xl font-bold text-blue-700">{stats.confirmed}</div>
              <div className="text-sm text-blue-600 flex items-center">
                <CheckCircle className="h-3 w-3 mr-1" />
                Confirmed
              </div>
            </div>
            <div className="bg-purple-50 p-4 rounded-xl border border-purple-200 shadow-sm">
              <div className="text-2xl font-bold text-purple-700">{stats.processing}</div>
              <div className="text-sm text-purple-600 flex items-center">
                <RefreshCw className="h-3 w-3 mr-1" />
                Processing
              </div>
            </div>
            <div className="bg-indigo-50 p-4 rounded-xl border border-indigo-200 shadow-sm">
              <div className="text-2xl font-bold text-indigo-700">{stats.shipped}</div>
              <div className="text-sm text-indigo-600 flex items-center">
                <Truck className="h-3 w-3 mr-1" />
                Shipped
              </div>
            </div>
            <div className="bg-green-50 p-4 rounded-xl border border-green-200 shadow-sm">
              <div className="text-2xl font-bold text-green-700">{stats.delivered}</div>
              <div className="text-sm text-green-600 flex items-center">
                <CheckCircle className="h-3 w-3 mr-1" />
                Delivered
              </div>
            </div>
            <div className="bg-red-50 p-4 rounded-xl border border-red-200 shadow-sm">
              <div className="text-2xl font-bold text-red-700">{stats.cancelled}</div>
              <div className="text-sm text-red-600 flex items-center">
                <XCircle className="h-3 w-3 mr-1" />
                Cancelled
              </div>
            </div>
            <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-200 shadow-sm">
              <div className="text-2xl font-bold text-emerald-700">{formatINR(stats.totalRevenue)}</div>
              <div className="text-sm text-emerald-600 flex items-center">
                <TrendingUp className="h-3 w-3 mr-1" />
                Revenue
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-8">
          <div className="p-6">
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search orders by product, buyer, seller, color, or order ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
              </div>

              {/* Status Filter */}
              <div className="w-full lg:w-64">
                <div className="relative">
                  <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 appearance-none"
                  >
                    <option value="all">All Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="processing">Processing</option>
                    <option value="shipped">Shipped</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Orders List */}
        {filteredOrders.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Package className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Orders Found</h3>
            <p className="text-gray-600">
              {searchTerm || statusFilter !== 'all' 
                ? 'Try adjusting your search or filter criteria'
                : 'No orders have been placed yet'}
            </p>
          </div>
        ) : (
          <>
            {/* Orders Count */}
            <div className="mb-4 text-sm text-gray-600">
              Showing {filteredOrders.length} of {orders.length} total orders
            </div>
            
            {/* Orders Grid */}
            <div className="space-y-4">
              {filteredOrders.map((order) => {
                const StatusIcon = statusConfig[order.orderStatus]?.icon || AlertCircle;
                const statusColor = statusConfig[order.orderStatus]?.color || 'bg-gray-100 text-gray-800';
                
                return (
                  <div key={order.id} className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition-shadow overflow-hidden">
                    {/* Order Header */}
                    <div 
                      className="p-6 cursor-pointer hover:bg-gray-50"
                      onClick={() => toggleOrderDetails(order.id)}
                    >
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-start gap-4">
                            {order.productImage ? (
                              <img
                                src={order.productImage}
                                alt={order.productName}
                                className="w-16 h-16 rounded-lg object-cover border border-gray-200"
                              />
                            ) : (
                              <div className="w-16 h-16 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center">
                                <Package className="h-8 w-8 text-gray-400" />
                              </div>
                            )}
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <h3 className="font-semibold text-gray-900 text-lg">
                                    {order.productName || 'Unnamed Product'}
                                  </h3>
                                  
                                  {/* NEW: Show selected color badge in header if exists */}
                                  {order.selectedColor && (
                                    <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full font-medium flex items-center">
                                      <Palette className="h-3 w-3 mr-1" />
                                      {order.selectedColor}
                                    </span>
                                  )}
                                </div>
                                <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center ${statusColor}`}>
                                  <StatusIcon className="h-3 w-3 mr-1.5" />
                                  {statusConfig[order.orderStatus]?.label || order.orderStatus || 'Unknown'}
                                </span>
                              </div>
                              
                              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm text-gray-600">
                                <div className="flex items-center">
                                  <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                                  <span>{formatDate(order.createdAt)}</span>
                                </div>
                                <div className="flex items-center">
                                  <ShoppingBag className="h-4 w-4 mr-2 text-gray-400" />
                                  <span>Order ID: {order.orderId?.substring(0, 8) || 'N/A'}</span>
                                </div>
                                <div className="flex items-center">
                                  <User className="h-4 w-4 mr-2 text-gray-400" />
                                  <span>Buyer: {order.buyerName || 'Unknown'}</span>
                                </div>
                                <div className="flex items-center">
                                  <Building className="h-4 w-4 mr-2 text-gray-400" />
                                  <span>Seller: {order.sellerName || 'Unknown'}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="flex flex-col items-end">
                          <div className="text-right mb-2">
                            <div className="text-2xl font-bold text-orange-600">
                              {formatINR(order.totalAmount || 0)}
                            </div>
                            <p className="text-sm text-gray-600">
                              {order.quantity || 1} × {formatINR(order.unitPrice || 0)}
                            </p>
                          </div>
                          <ChevronRight className={`h-5 w-5 text-gray-400 transition-transform ${expandedOrder === order.id ? 'rotate-90' : ''}`} />
                        </div>
                      </div>
                    </div>

                    {/* Order Details (Expanded) */}
                    {expandedOrder === order.id && (
                      <div className="border-t border-gray-200 px-6 py-6 bg-gray-50">
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                          {/* Order Details */}
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                              <FileText className="h-5 w-5 mr-2 text-orange-600" />
                              Order Details
                            </h4>
                            <div className="space-y-2 text-sm bg-white p-4 rounded-lg border">
                              <div className="flex justify-between">
                                <span className="text-gray-600">Product Name:</span>
                                <span className="font-medium text-right">{order.productName || 'N/A'}</span>
                              </div>
                              
                              {/* NEW: Display Selected Color */}
                              {order.selectedColor && (
                                <div className="flex justify-between items-center">
                                  <span className="text-gray-600 flex items-center">
                                    <Palette className="h-4 w-4 mr-2 text-gray-400" />
                                    Selected Color:
                                  </span>
                                  <span className="font-medium px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm flex items-center">
                                    <Palette className="h-3 w-3 mr-1.5" />
                                    {order.selectedColor}
                                  </span>
                                </div>
                              )}
                              
                              {/* NEW: Display All Available Colors */}
                              {order.productColors && order.productColors.trim() !== '' && (
                                <div className="flex justify-between items-start">
                                  <span className="text-gray-600 flex items-start pt-1">
                                    <Palette className="h-4 w-4 mr-2 text-gray-400 flex-shrink-0 mt-0.5" />
                                    Available Colors:
                                  </span>
                                  <div className="text-right">
                                    <span className="font-medium text-sm mb-1 block">
                                      {order.productColors}
                                    </span>
                                    {order.availableColors && order.availableColors.length > 0 && (
                                      <div className="flex flex-wrap gap-1.5 justify-end mt-1">
                                        {order.availableColors.map((color, idx) => (
                                          <span 
                                            key={idx} 
                                            className={`px-2 py-0.5 text-xs rounded-full ${order.selectedColor === color
                                              ? 'bg-orange-100 text-orange-800 border border-orange-300'
                                              : 'bg-gray-100 text-gray-700'
                                            }`}
                                          >
                                            {color}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                              
                              <div className="flex justify-between">
                                <span className="text-gray-600">Order ID:</span>
                                <span className="font-medium">{order.orderId || 'N/A'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Quantity:</span>
                                <span className="font-medium">{order.quantity || 0} {order.productUnit || 'units'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Unit Price:</span>
                                <span className="font-medium">{formatINR(order.unitPrice || 0)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Total Amount:</span>
                                <span className="font-bold text-orange-600">{formatINR(order.totalAmount || 0)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Order Type:</span>
                                <span className="font-medium capitalize">{order.orderType || 'direct_order'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Created:</span>
                                <span className="font-medium">{formatDate(order.createdAt)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Last Updated:</span>
                                <span className="font-medium">{formatDate(order.updatedAt)}</span>
                              </div>
                            </div>
                          </div>

                          {/* Buyer Information */}
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                              <User className="h-5 w-5 mr-2 text-blue-600" />
                              Buyer Information
                            </h4>
                            <div className="space-y-2 text-sm bg-white p-4 rounded-lg border">
                              <div className="flex justify-between">
                                <span className="text-gray-600">Name:</span>
                                <span className="font-medium">{order.buyerName || 'N/A'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Email:</span>
                                <span className="font-medium">{order.buyerEmail || 'N/A'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Phone:</span>
                                <span className="font-medium">{order.buyerPhone || 'N/A'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Buyer ID:</span>
                                <span className="font-medium text-xs">{order.buyerId?.substring(0, 8) || 'N/A'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Company:</span>
                                <span className="font-medium">{order.buyerCompany || 'N/A'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Location:</span>
                                <span className="font-medium">{order.buyerLocation || 'N/A'}</span>
                              </div>
                            </div>
                          </div>

                          {/* Seller Information */}
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                              <Building className="h-5 w-5 mr-2 text-green-600" />
                              Seller Information
                            </h4>
                            <div className="space-y-2 text-sm bg-white p-4 rounded-lg border">
                              <div className="flex justify-between">
                                <span className="text-gray-600">Name:</span>
                                <span className="font-medium">{order.sellerName || 'N/A'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Email:</span>
                                <span className="font-medium">{order.sellerEmail || 'N/A'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Phone:</span>
                                <span className="font-medium">{order.sellerPhone || 'N/A'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Seller ID:</span>
                                <span className="font-medium text-xs">{order.sellerId?.substring(0, 8) || 'N/A'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Business:</span>
                                <span className="font-medium">{order.sellerBusiness || 'N/A'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Payment Status:</span>
                                <span className={`font-medium ${order.paymentStatus === 'paid' ? 'text-green-600' : 'text-yellow-600'}`}>
                                  {(order.paymentStatus || 'pending').toUpperCase()}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Color Information Section - NEW */}
                        {(order.selectedColor || order.productColors) && (
                          <div className="mt-6 bg-white rounded-lg border border-gray-200 p-4">
                            <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                              <Palette className="h-5 w-5 mr-2 text-orange-600" />
                              Color Information
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {order.selectedColor && (
                                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                                  <div className="flex items-center mb-2">
                                    <Palette className="h-4 w-4 mr-2 text-blue-600" />
                                    <div className="font-medium text-blue-800">Selected Color by Buyer</div>
                                  </div>
                                  <div className="flex items-center justify-between">
                                    <span className="text-blue-700 text-sm">
                                      The buyer selected this color when placing the order
                                    </span>
                                    <span className="px-3 py-1 bg-blue-600 text-white rounded-full font-medium">
                                      {order.selectedColor}
                                    </span>
                                  </div>
                                </div>
                              )}
                              
                              {order.productColors && order.productColors.trim() !== '' && (
                                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                  <div className="flex items-center mb-2">
                                    <Palette className="h-4 w-4 mr-2 text-gray-600" />
                                    <div className="font-medium text-gray-800">All Available Colors</div>
                                  </div>
                                  <div>
                                    <div className="text-gray-700 text-sm mb-2">
                                      All colors available for this product as listed by the seller
                                    </div>
                                    <div className="text-gray-900 font-medium">
                                      {order.productColors}
                                    </div>
                                    {order.availableColors && order.availableColors.length > 0 && (
                                      <div className="flex flex-wrap gap-1.5 mt-2">
                                        {order.availableColors.map((color, idx) => (
                                          <div 
                                            key={idx} 
                                            className={`px-2 py-1 text-xs rounded-full flex items-center ${order.selectedColor === color
                                              ? 'bg-orange-100 text-orange-800 border border-orange-300 font-medium'
                                              : 'bg-white text-gray-700 border border-gray-300'
                                            }`}
                                          >
                                            {order.selectedColor === color && (
                                              <CheckCircle className="h-3 w-3 mr-1" />
                                            )}
                                            {color}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Admin Actions */}
                        <div className="mt-6 pt-4 border-t border-gray-200">
                          <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                            <Shield className="h-5 w-5 mr-2 text-red-600" />
                            Admin Actions
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {/* Update Status Dropdown */}
                            <div className="relative">
                              <select
                                value={order.orderStatus}
                                onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                                className="px-3 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 text-sm font-medium appearance-none pr-8"
                              >
                                <option value="pending">Mark as Pending</option>
                                <option value="confirmed">Mark as Confirmed</option>
                                <option value="processing">Mark as Processing</option>
                                <option value="shipped">Mark as Shipped</option>
                                <option value="delivered">Mark as Delivered</option>
                                <option value="cancelled">Mark as Cancelled</option>
                              </select>
                              <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                            </div>

                            {/* Action Buttons */}
                            <button
                              onClick={() => deleteOrder(order.id)}
                              className="px-3 py-2 border border-red-200 text-red-600 rounded-lg hover:bg-red-50 flex items-center text-sm font-medium"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete Order
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminOrdersPage;